import { InvalidTransitionError } from "./errors.js";
/**
 * Defines a finite state machine as an explicit transition table.
 * The table is validated once at definition time: unknown states or events fail fast.
 */
export function defineMachine(definition) {
    const { name, states, events, initial, transitions } = definition;
    const stateSet = new Set(states);
    const eventSet = new Set(events);
    if (!stateSet.has(initial)) {
        throw new Error(`${name}: initial state "${initial}" is not declared`);
    }
    for (const [from, row] of Object.entries(transitions)) {
        if (!stateSet.has(from))
            throw new Error(`${name}: unknown state "${from}" in transitions`);
        for (const [event, to] of Object.entries(row)) {
            if (!eventSet.has(event))
                throw new Error(`${name}: unknown event "${event}" in state "${from}"`);
            if (to === undefined || !stateSet.has(to)) {
                throw new Error(`${name}: event "${event}" in state "${from}" targets unknown state "${String(to)}"`);
            }
        }
    }
    for (const state of states) {
        if (!(state in transitions))
            throw new Error(`${name}: state "${state}" has no transition row`);
    }
    const next = (state, event) => transitions[state][event];
    const terminal = states.filter((state) => Object.keys(transitions[state]).length === 0);
    return Object.freeze({
        name,
        states,
        events,
        initial,
        terminal,
        transition(state, event) {
            const target = next(state, event);
            if (target === undefined)
                throw new InvalidTransitionError(name, state, event);
            return target;
        },
        can(state, event) {
            return next(state, event) !== undefined;
        },
        allowedEvents(state) {
            return events.filter((event) => next(state, event) !== undefined);
        },
        isTerminal(state) {
            return terminal.includes(state);
        },
    });
}
//# sourceMappingURL=state-machine.js.map